import {
  AIAgentConversation,
  AIAgentIdentityStatus,
  AIAgentMessage,
  AIAgentMessageAction,
  AIAgentMessageRole,
  AIAgentMessageState,
  AIAgentMode,
  AIAgentScreenContext,
} from '../AIAgentTypes';

export const AI_AGENT_CONVERSATION_STATE_STORAGE_KEY =
  'sgd-ai-agent-conversation-state';

const STORAGE_VERSION = 1;
const INTERRUPTED_MESSAGE =
  'A resposta foi interrompida pelo recarregamento da página. Envie a pergunta novamente se necessário.';

interface StoredConversationState {
  version: typeof STORAGE_VERSION;
  conversationId: string;
  conversationEpoch: number;
  mode: AIAgentMode;
  availability: AIAgentConversation['availability'];
  messages: AIAgentMessage[];
  draft: string;
  sessionCreatedAt?: string;
}

const VALID_MODES = new Set<AIAgentMode>(['closed', 'normal', 'expanded']);
const VALID_ROLES = new Set<AIAgentMessageRole>([
  'welcome',
  'user',
  'assistant',
  'status',
  'error',
]);
const VALID_STATES = new Set<AIAgentMessageState>([
  'settled',
  'thinking',
  'recoverable-error',
  'terminal-error',
]);
const VALID_ACTIONS = new Set<AIAgentMessageAction>([
  'none',
  'retry',
  'start-new-conversation',
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function isNonNegativeInteger(value: unknown): value is number {
  return Number.isInteger(value) && Number(value) >= 0;
}

function isOptionalString(value: unknown): value is string | undefined {
  return value === undefined || typeof value === 'string';
}

function isValidMessage(value: unknown): value is AIAgentMessage {
  if (!isRecord(value)) {
    return false;
  }

  return (
    typeof value.id === 'string' &&
    isNonNegativeInteger(value.conversationEpoch) &&
    typeof value.role === 'string' &&
    VALID_ROLES.has(value.role as AIAgentMessageRole) &&
    typeof value.content === 'string' &&
    typeof value.state === 'string' &&
    VALID_STATES.has(value.state as AIAgentMessageState) &&
    typeof value.createdAt === 'string' &&
    typeof value.action === 'string' &&
    VALID_ACTIONS.has(value.action as AIAgentMessageAction) &&
    isOptionalString(value.questionId) &&
    isOptionalString(value.requestId)
  );
}

function parseStoredState(raw: string): StoredConversationState | null {
  try {
    const value: unknown = JSON.parse(raw);
    if (!isRecord(value)) {
      return null;
    }

    if (
      value.version !== STORAGE_VERSION ||
      typeof value.conversationId !== 'string' ||
      !value.conversationId.trim() ||
      !isNonNegativeInteger(value.conversationEpoch) ||
      typeof value.mode !== 'string' ||
      !VALID_MODES.has(value.mode as AIAgentMode) ||
      (value.availability !== 'active' &&
        value.availability !== 'conversation-limit') ||
      !Array.isArray(value.messages) ||
      value.messages.length === 0 ||
      !value.messages.every(isValidMessage) ||
      typeof value.draft !== 'string' ||
      !isOptionalString(value.sessionCreatedAt)
    ) {
      return null;
    }

    return value as unknown as StoredConversationState;
  } catch {
    return null;
  }
}

function normalizeMessageAfterReload(message: AIAgentMessage): AIAgentMessage {
  if (message.state !== 'thinking') {
    return message;
  }

  return {
    ...message,
    role: 'error',
    content: INTERRUPTED_MESSAGE,
    state: 'terminal-error',
    action: 'none',
  };
}

export function clearStoredConversationState(): void {
  try {
    window.sessionStorage.removeItem(AI_AGENT_CONVERSATION_STATE_STORAGE_KEY);
  } catch {
    // Storage pode estar indisponível; a conversa continua somente em memória.
  }

  try {
    window.localStorage.removeItem(AI_AGENT_CONVERSATION_STATE_STORAGE_KEY);
  } catch {
    // Remove também qualquer valor legado que tenha sido persistido fora da aba.
  }
}

export function persistConversationState(state: AIAgentConversation): void {
  const conversationId = state.session.conversationId?.trim();
  if (!conversationId || state.session.status !== 'active') {
    return;
  }

  const storedState: StoredConversationState = {
    version: STORAGE_VERSION,
    conversationId,
    conversationEpoch: state.conversationEpoch,
    mode: state.mode,
    availability: state.availability,
    messages: state.messages,
    draft: state.draft,
    sessionCreatedAt: state.session.createdAt,
  };

  try {
    window.sessionStorage.setItem(
      AI_AGENT_CONVERSATION_STATE_STORAGE_KEY,
      JSON.stringify(storedState)
    );
  } catch {
    // Falha de quota/privacidade não deve impedir o uso do assistente.
  }
}

export function restoreConversationState(
  conversationId: string | null,
  screenContext: AIAgentScreenContext,
  identityStatus: AIAgentIdentityStatus
): AIAgentConversation | null {
  if (!conversationId) {
    clearStoredConversationState();
    return null;
  }

  let raw: string | null = null;
  try {
    raw = window.sessionStorage.getItem(AI_AGENT_CONVERSATION_STATE_STORAGE_KEY);
  } catch {
    return null;
  }

  if (!raw) {
    return null;
  }

  const storedState = parseStoredState(raw);
  if (!storedState || storedState.conversationId !== conversationId) {
    clearStoredConversationState();
    return null;
  }

  return {
    mode: storedState.mode,
    phase: 'idle',
    availability: storedState.availability,
    identityStatus,
    conversationEpoch: storedState.conversationEpoch,
    session: {
      conversationId,
      conversationEpoch: storedState.conversationEpoch,
      status: 'active',
      createdAt: storedState.sessionCreatedAt,
    },
    messages: storedState.messages.map(normalizeMessageAfterReload),
    draft: storedState.draft,
    screenContext,
  };
}
